import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  
  ChevronLeft,
  ChevronRight,
  
  Wine,
  Snowflake,
  MapPin,
  Gift,
  Beer,
  Music,
  Star,
  CalendarDays,
  Info,
  TreePine,
  Crown,
  PartyPopper,
  Ticket,
  Cookie,
} from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { GygTourBand } from "@/components/GetYourGuide";

// The Tivoli Christmas ticket (entry + ride pass) — the seasonal product,
// used for both the market card and the big booking widget.
const TIVOLI_XMAS_TICKET_URL =
  "https://www.getyourguide.dk/kobenhavn-l12/kobenhavn-jule-entrebillet-til-tivoli-og-pas-til-forlystelser-t1427049/?partner_id=HIOKDT8&utm_medium=online_publisher";
import { Seo } from "@/components/Seo";
import { Helmet } from "react-helmet-async";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ChristmasWalkCard } from "@/components/ChristmasWalk";

const SITE = "https://cphhighlights.com";
const mapsUrl = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

// The Christmas walk route data lives in data/christmasWalk.js — shared with
// the dedicated /christmas-walk page (which carries the ItemList JSON-LD) and
// mirrored in the planner as the one-tap XMAS_WALK day, Nov–Dec.

const HERO_IMG =
  "https://images.pexels.com/photos/34913181/pexels-photo-34913181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Christmas photo slider — own photos from the city in December.
const XMAS_SLIDES = [
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/nv0h6ujj_2026-09-01%2020_01_05.749%2B0200.webp",
    alt: "Nyhavn's restaurants lit up with Christmas hearts and garlands",
    caption: "Nyhavn in December",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/pt1ktfs5_2026-09-01%2020_02_22.109%2B0200.webp",
    alt: "Christmas garlands and hearts over the cobbled shopping streets",
    caption: "The city centre all dressed up",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/87stjx03_2026-09-01%2020_02_40.292%2B0200.webp",
    alt: "Sailboats wrapped in fairy lights along Nyhavn's quay",
    caption: "Lights on the harbour",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/g5j8fs7t_2026-09-01%2020_03_05.043%2B0200.webp",
    alt: "Copenhageners at an outdoor café under heat lamps and Christmas decorations",
    caption: "Outdoor hygge season",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/spxg7p2f_2026-09-01%2020_04_09.044%2B0200.webp",
    alt: "Star-shaped Christmas lights strung across a Copenhagen side street",
    caption: "Stars over the side streets",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/unwk541m_2026-09-01%2020_04_48.880%2B0200.webp",
    alt: "A giant chandelier of Christmas lights hanging over a Copenhagen shopping street",
    caption: "A ceiling of lights",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/78kl31ob_2026-09-03%2000_47_45.805%2B0200.webp",
    alt: "Hotel d'Angleterre's façade wrapped in golden Christmas lights with a huge Christmas tree outside",
    caption: "d'Angleterre goes full gold",
  },
];

function XmasSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = XMAS_SLIDES.length;
  const go = (i) => setIndex((i + count) % count);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(t);
  }, [paused, count]);

  return (
    <section
      className="bg-black"
      data-testid="christmas-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative max-w-[1400px] mx-auto overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {XMAS_SLIDES.map((s) => (
            <div key={s.src} className="relative w-full shrink-0 aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]">
              <img src={s.src} alt={s.alt} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <p
                data-testid="christmas-slider-caption"
                className="absolute bottom-5 left-5 md:bottom-8 md:left-10 font-display font-900 tracking-tight text-white text-xl md:text-3xl"
              >
                {s.caption}
              </p>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="christmas-slider-prev"
          aria-label="Previous photo"
          onClick={() => go(index - 1)}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/25 text-white flex items-center justify-center backdrop-blur-sm hover:bg-white hover:text-black transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          data-testid="christmas-slider-next"
          aria-label="Next photo"
          onClick={() => go(index + 1)}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 border border-white/25 text-white flex items-center justify-center backdrop-blur-sm hover:bg-white hover:text-black transition-colors"
        >
          <ChevronRight size={20} />
        </button>
        <div className="absolute bottom-5 right-5 md:bottom-8 md:right-10 flex gap-2">
          {XMAS_SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`christmas-slider-dot-${i}`}
              aria-label={`Go to photo ${i + 1}`}
              onClick={() => go(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                i === index ? "bg-[#FDE047]" : "bg-white/50 hover:bg-white"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// Structured data — concrete 2026 dates for the season's events.
const SCHEMA_EVENTS = [
  { name: "Christmas at Tivoli Gardens", start: "2026-11-13", end: "2027-01-03", venue: "Tivoli Gardens", street: "Vesterbrogade 3", locality: "Copenhagen",
    desc: "Tivoli transforms into a wonderland of a million lights, festive stalls, gløgg and roasted almonds — one of the most beautiful Christmas sights in Copenhagen.",
    img: "https://images.unsplash.com/photo-1701944578198-7f0a4b099b45?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    ticket: "https://www.tivoli.dk/en/jul" },
  { name: "Christmas at Bakken", start: "2026-11-21", end: "2026-12-20", venue: "Dyrehavsbakken (Bakken)", street: "Dyrehavevej 62", locality: "Klampenborg",
    desc: "The world's oldest amusement park goes full Christmas at weekends — twinkling lights, a proper Christmas market and an outdoor ice-skating rink in the deer forest north of the city. Free entry.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/fj9sbklv_2026-08-19%2000_56_22.292%2B0200.webp" },
  { name: "Gammel Torv Christmas Market", start: "2026-11-06", end: "2026-12-21", venue: "Gammel Torv", locality: "Copenhagen",
    desc: "The charming old-town stalls on Gammel Torv open the season first (6 November) — right in the middle of the shopping streets. Open Mon–Thu 11:00–19:00, Fri–Sat 11:00–21:00, Sun 12:00–19:00.",
    img: "https://cphhighlights.com/christmas/reindeer.webp" },
  { name: "Rådhuspladsen Christmas Market", start: "2026-11-11", end: "2026-12-21", venue: "Rådhuspladsen", locality: "Copenhagen",
    desc: "The big Christmas market on City Hall Square (Rådhuspladsen) — a classic central market in the middle of the town hall square.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/rn2xhl0i_2026-08-19%2000_54_54.578%2B0200.webp" },
  { name: "Jul på Højbro Plads", start: "2026-11-14", end: "2026-12-20", venue: "Højbro Plads", locality: "Copenhagen",
    desc: "A brand-new Christmas market for 2026 on Højbro Plads — cosy stalls and stories from across the Danish Realm, with music, Christmas entertainment and activities for the whole family. Open Mon–Wed 11:00–19:00, Thu 11:00–20:00, Fri–Sat 11:00–21:00, Sun 12:00–19:00.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/357ef4775a619a15_2026-10-14%2014_41_54.868%2B0300.jpg" },
  { name: "Nyhavn Christmas Market", start: "2026-11-01", end: "2026-12-23", venue: "Nyhavn", locality: "Copenhagen",
    desc: "The postcard harbour draped in lights, with wooden stalls lining the quay in front of the coloured houses. Copenhagen hygge — have a gløgg on one of the restaurant terraces and soak it in. Dates aren't official — it typically lights up around 1 November.",
    img: "https://images.unsplash.com/photo-1604947104624-e8682b14931c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { name: "Christiania Christmas Market", start: "2026-12-06", end: "2026-12-20", venue: "Freetown Christiania", locality: "Copenhagen",
    desc: "The cosiest, most alternative market in town — handmade goods, warming food and live music inside the Grey Hall at Freetown Christiania. Well worth the trip over to Christianshavn.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/mvbafil8_2026-08-19%2000_56_37.338%2B0200.webp" },
  { name: "J-Day (J-dag) — Tuborg Julebryg release", start: "2026-11-06T20:59:00+01:00", end: "2026-11-07T01:00:00+01:00", venue: "Bars across the city", locality: "Copenhagen",
    desc: "Tuborg releases the year's Christmas beer (Julebryg) at 20:59 sharp — bars across the city celebrate, and blue Santa hats appear everywhere.",
    img: "https://images.pexels.com/photos/34913181/pexels-photo-34913181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Copenhagen Christmas Procession", start: "2026-11-28", end: "2026-11-28", venue: "Amagerbrogade", locality: "Copenhagen",
    desc: "A thousand Santas, floats and marching bands parade down Amagerbrogade on the last Saturday of November.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/pt1ktfs5_2026-09-01%2020_02_22.109%2B0200.webp" },
  { name: "Royal Copenhagen Christmas Tables", start: "2026-11-18", end: "2026-12-31", venue: "Royal Copenhagen Flagship Store", street: "Amagertorv 6", locality: "Copenhagen",
    desc: "Royal Copenhagen's beloved exhibition of artist-designed Christmas tables at the flagship store on Amagertorv — a tradition since 1963.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/spxg7p2f_2026-09-01%2020_04_09.044%2B0200.webp" },
  { name: "Santa Lucia Parade & Lucia i Kajak", start: "2026-12-13", end: "2026-12-13", venue: "Tivoli Gardens & the Copenhagen canals", locality: "Copenhagen",
    desc: "Candle-lit Lucia procession on 13 December — in Tivoli and as 'Lucia i Kajak', where kayaks drift the canals in song.",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/87stjx03_2026-09-01%2020_02_40.292%2B0200.webp" },
];

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Christmas in Copenhagen", item: `${SITE}/christmas` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Christmas in Copenhagen 2026: Markets, Tivoli & Things to Do",
  url: `${SITE}/christmas`,
  description:
    "Christmas in Copenhagen 2026 — the whole city turns cosy: every Christmas market with dates, the lit-up streets, where to find the city's best gløgg, Tivoli's million lights and practical December tips.",
  datePublished: "2026-09-01",
  dateModified: "2026-11-02",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const EVENTS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Christmas events in Copenhagen 2026",
  itemListElement: SCHEMA_EVENTS.map((e, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Event",
      name: e.name,
      description: e.desc,
      image: [e.img],
      startDate: e.start,
      endDate: e.end,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      url: `${SITE}/christmas`,
      offers: e.ticket
        ? {
            "@type": "Offer",
            url: e.ticket,
            availability: "https://schema.org/InStock",
            validFrom: e.start,
          }
        : {
            "@type": "Offer",
            price: "0",
            priceCurrency: "DKK",
            availability: "https://schema.org/InStock",
            url: `${SITE}/christmas`,
            validFrom: e.start,
          },
      location: {
        "@type": "Place",
        name: e.venue,
        address: {
          "@type": "PostalAddress",
          ...(e.street ? { streetAddress: e.street } : {}),
          addressLocality: e.locality,
          addressCountry: "DK",
        },
      },
      organizer: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
    },
  })),
};

const MARKETS = [
  {
    name: "Tivoli Gardens",
    ticket: TIVOLI_XMAS_TICKET_URL,
    dates: "13 November – 3 January",
    open: [11, 13], close: [1, 3],
    address: "Vesterbrogade 3",
    img: "https://images.unsplash.com/photo-1701944578198-7f0a4b099b45?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body:
      "Tivoli transforms into a wonderland of lights, festive stalls, gløgg and Christmas treats — genuinely one of the most amazing experiences you'll have in Copenhagen at Christmas.",
  },
  {
    name: "Gammel Torv",
    dates: "6 November – 21 December",
    open: [11, 6], close: [12, 21],
    address: "Gammel Torv",
    img: "/christmas/reindeer.webp",
    body:
      "The charming old-town stalls on Gammel Torv open the season first — right in the middle of the shopping streets.",
    hours: "Mon–Thu 11:00 – 19:00\nFri–Sat 11:00 – 21:00\nSun 12:00 – 19:00",
  },
  {
    name: "Rådhuspladsen",
    dates: "11 November – 21 December",
    open: [11, 11], close: [12, 21],
    address: "Rådhuspladsen",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/rn2xhl0i_2026-08-19%2000_54_54.578%2B0200.webp",
    body:
      "The big one on City Hall Square follows on 11 November — a classic central market in the middle of the town hall square.",
  },
  {
    name: "Jul på Højbro Plads",
    dates: "14 November – 20 December",
    open: [11, 14], close: [12, 20],
    address: "Højbro Plads",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/357ef4775a619a15_2026-10-14%2014_41_54.868%2B0300.jpg",
    body:
      "New for 2026 — a market on Højbro Plads with cosy stalls and stories from across the Danish Realm. Music, Christmas entertainment and family activities, with traditions, tastes and tales from Greenland, the Faroe Islands and Denmark.",
    hours: "Mon–Wed 11:00 – 19:00\nThu 11:00 – 20:00\nFri–Sat 11:00 – 21:00\nSun 12:00 – 19:00",
  },
  {
    name: "Nyhavn",
    dates: "Not official — typically from around 1 Nov",
    open: [11, 1], close: [12, 31],
    address: "Nyhavn",
    img: "https://images.unsplash.com/photo-1604947104624-e8682b14931c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body:
      "The postcard harbour draped in lights, with wooden stalls lining the quay in front of the coloured houses. Copenhagen hygge — have a gløgg on one of the restaurant terraces and soak it in.",
    link: { label: "Explore Indre By", to: "/areas/indre-by" },
  },
  {
    name: "Christiania",
    dates: "8 – 20 December",
    open: [12, 8], close: [12, 20],
    maps: "Freetown Christiania, Copenhagen",
    address: "Prinsessegade, Christianshavn",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/mvbafil8_2026-08-19%2000_56_37.338%2B0200.webp",
    body:
      "The cosiest, most alternative market in town — handmade goods, warming food and live music inside the Grey Hall at Freetown Christiania. Well worth the trip over to Christianshavn.",
    link: { label: "The Christianshavn guide", to: "/areas/christianshavn" },
  },
  {
    name: "Bakken",
    dates: "Weekends, 21 November – 20 December 2026",
    open: [11, 21], close: [12, 20],
    maps: "Dyrehavsbakken, Klampenborg",
    address: "Dyrehavsbakken, Klampenborg",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/fj9sbklv_2026-08-19%2000_56_22.292%2B0200.webp",
    body:
      "The world's oldest amusement park goes full Christmas at weekends — twinkling lights, a proper Christmas market and an outdoor ice-skating rink out in the deer forest north of the city.",
    link: { label: "The Bakken day-trip guide", to: "/trips/bakken" },
  },
];

const DECOR = [
  {
    icon: Crown,
    name: "Hotel d'Angleterre",
    where: "Kongens Nytorv",
    body:
      "The grand hotel's façade becomes the city's most photographed Christmas display — an enormous illuminated installation over the entrance that changes theme each year. The lights are switched on 13 November at 16:45.",
  },
  {
    icon: Gift,
    name: "Magasin du Nord",
    where: "Kongens Nytorv",
    body:
      "The historic department store dresses its facade in light and inside is real old-world Christmas-shopping atmosphere. Lights turn on 6 November at 17.00.",
  },
  {
    icon: TreePine,
    name: "The big Christmas tree",
    where: "City Hall Square",
    body:
      "A towering, brightly lit Christmas tree stands on Rådhuspladsen all season — the classic meeting point in the heart of town. It's traditionally lit on the first Sunday of Advent, which in 2026 is Sunday 29 November.",
  },
];

const ACTIVITIES = [
  {
    icon: Beer,
    name: "J-Day (J-dag)",
    when: "20:59, the first Friday of November",
    body:
      "The event that kick-starts the whole Danish Christmas. At 20:59 on the first Friday of November, Tuborg (part of Carlsberg) releases its limited-edition Christmas beer, Julebryg. Blue-clad delivery trucks and 'Julebryg' hats pour into bars across the city — a raucous, only-in-Denmark start to the season.",
  },
  {
    icon: PartyPopper,
    name: "Christmas Procession",
    when: "Last Saturday of November · Amagerbrogade",
    body:
      "A huge parade on foot and on wheels rolls down Amagerbrogade with over 1,000 participants and hundreds of unique vehicles — one of the city's biggest festive gatherings.",
  },
  {
    icon: Star,
    name: "Royal Copenhagen Christmas Tables",
    when: "18 November – 31 December · Amagertorv",
    body:
      "A must-see tradition running every year since 1963 at the Royal Copenhagen flagship store on Amagertorv. Each beautifully set table follows a different theme, celebrating unique Danish Christmas traditions. Head up to the store's 2nd floor.",
  },
  {
    icon: Music,
    name: "Santa Lucia Parade",
    when: "13 December",
    body:
      "On Lucia Day, candle-lit processions take place at Tivoli, while 'Lucia i Kajak' sees kayakers — decorated with lights and garlands — glide through the canals singing Christmas carols in Nyhavn, Christianshavn and by Højbro Plads.",
  },
];

const LIGHT_STREETS = [
  "Strøget",
  "Købmagergade",
  "Frederiksborggade",
  "Hovedvagtsgade",
  "Ny Østergade",
  "Pilestræde",
  "Skoubogade",
  "Kronprinsensgade",
  "Læderstræde",
  "Hyskenstræde",
  "Kompagnistræde",
  "Studiestræde",
  "Larsbjørnsstræde",
  "Teglgårdsstræde",
  "Nørregade",
  "Fiolstræde",
  "Rosengården",
];

// Days until the event's next opening, or {open:true} if it's running now.
// `open`/`close` are [month, day] (close may wrap into January).
const countdownFor = ({ open, close }) => {
  if (!open) return { tba: true };
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear();
  for (const sy of [y - 1, y]) {
    const start = new Date(sy, open[0] - 1, open[1]);
    let end = close ? new Date(sy, close[0] - 1, close[1]) : start;
    if (end < start) end = new Date(sy + 1, close[0] - 1, close[1]);
    if (today >= start && today <= end) return { open: true };
  }
  let start = new Date(y, open[0] - 1, open[1]);
  if (start < today) start = new Date(y + 1, open[0] - 1, open[1]);
  return { days: Math.round((start - today) / 86400000) };
};

export default function ChristmasPage() {
  return (
    <main data-testid="christmas-page" className="pt-16 md:pt-20">
      <Seo
        title="Copenhagen Christmas Markets 2026: Dates, Tivoli & Gløgg"
        description="Copenhagen Christmas markets 2026: every market with dates, Tivoli's lights, the best gløgg spots, J-Day and the Santa Lucia parade, plus what to pack."
        path="/christmas"
        image={HERO_IMG}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(EVENTS_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#7A1420] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="A snowy Christmas market glowing with lights in Tivoli Gardens, Copenhagen"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#7A1420] via-[#7A1420]/70 to-[#7A1420]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-8 md:pt-28 md:pb-10">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="christmas-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="christmas-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-white">
              ALL YOU NEED TO KNOW
            </p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Christmas in<br />
            <span className="font-editorial italic font-600">Copenhagen</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">
            From the start of November the city glows and hygge is all over. Gløgg servings and roasted
            almonds. Christmas in Copenhagen is cosy and down to earth, not overdone or flashy.
            Here&apos;s everything worth knowing and catching, from a local&apos;s point of view.
          </p>
          <p data-testid="christmas-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/40 mt-4">
            Last updated: 2 November 2026
          </p>
        </div>
      </section>

      {/* photo slider */}
      <XmasSlider />

      {/* markets */}
      <section id="markets" className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                CHRISTMAS MARKETS &amp; LIGHTS
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Every 2026 Christmas<br />
              <span className="font-editorial italic font-600">market in Copenhagen</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {MARKETS.map((m, i) => {
              const cd = m.open ? countdownFor({ open: m.open, close: m.close }) : null;
              return (
              <Reveal key={m.name} delay={i * 0.06} y={40}>
                <div
                  data-testid={`christmas-market-${i}`}
                  className="group bg-white border border-zinc-200 shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all overflow-hidden h-full flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={m.img}
                      alt={`${m.name} at Christmas in Copenhagen`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {cd && (cd.open || cd.days != null) && (
                      <span
                        data-testid={`christmas-market-countdown-${i}`}
                        className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#7A1420] text-white px-3 py-1.5 shadow-lg backdrop-blur-sm"
                      >
                        {cd.open ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FDE047] animate-pulse" />
                            <span className="font-display font-900 text-xs tracking-wide">OPEN NOW</span>
                          </>
                        ) : (
                          <>
                            <span className="font-display font-900 text-sm tabular-nums text-[#FDE047]">{cd.days}</span>
                            <span className="font-body font-800 text-[10px] tracking-widest uppercase">
                              {cd.days === 1 ? "day to go" : "days to go"}
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="p-4 md:p-6 flex flex-col flex-1">
                    <h3 className="font-display font-900 tracking-tight text-xl md:text-2xl leading-none">
                      {m.ticket ? (
                        <a
                          href={m.ticket}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          data-testid="christmas-market-tivoli-ticket"
                          className="underline decoration-[#DF3226]/40 underline-offset-4 hover: transition-colors"
                        >
                          {m.name}
                        </a>
                      ) : (
                        m.name
                      )}
                    </h3>
                    <p className="mt-1.5 md:mt-2 flex items-center gap-1.5 font-body text-sm text-black/50">
                      <MapPin size={13} className="text-[#DF3226] shrink-0" />
                      <a
                        href={mapsUrl(m.maps || `${m.address}, Copenhagen`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`christmas-market-maps-${i}`}
                        className="hover:text-[#DF3226] hover:underline underline-offset-4 transition-colors"
                      >
                        {m.address}
                      </a>
                    </p>
                    <span className="mt-1.5 md:mt-2 inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                      <CalendarDays size={13} /> {m.dates.toUpperCase()}
                    </span>
                    {m.hours && (
                      <p data-testid={`christmas-market-hours-${i}`} className="mt-1.5 md:mt-2 font-body text-xs md:text-sm text-black/50 leading-relaxed whitespace-pre-line">
                        {m.hours}
                      </p>
                    )}
                    <p className="font-body text-sm md:text-base text-black/70 mt-2 md:mt-3 leading-relaxed">
                      {m.body}
                    </p>
                    {m.link && (
                      <Link
                        to={m.link.to}
                        data-testid={`christmas-market-link-${i}`}
                        className="mt-3 inline-flex items-center gap-1.5 font-body font-700 text-sm text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226] w-fit"
                      >
                        {m.link.label}
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
              );
            })}
          </div>

          {/* practical December notes */}
          <Reveal y={20}>
            <div
              data-testid="christmas-december-notes"
              className="mt-10 bg-white border border-zinc-200 shadow-brut p-6 md:p-8 flex flex-col md:flex-row gap-6"
            >
              <div className="flex items-start gap-3 flex-1">
                <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                  <strong className="font-800 text-black">Markets on the squares only run until 21 December.</strong>{" "}
                  Keep in mind they close early Monday–Wednesday, at 19:00.
                </p>
              </div>
              <div className="flex items-start gap-3 flex-1">
                <Info size={18} className="text-[#DF3226] shrink-0 mt-0.5" />
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed">
                  <strong className="font-800 text-black">25–26 December:</strong> many things are closed and
                  transport runs at low capacity.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* decorations */}
      <section className="bg-[#7A1420] text-white border-y border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                WHERE TO SEE THE DECORATIONS
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Dressed up for<br />
              <span className="font-editorial italic font-600">the season</span>
            </h2>
            <p className="font-body text-sm md:text-base text-white/85 max-w-2xl mt-4 leading-relaxed">
              Fair warning: Copenhagen doesn&apos;t do the big, bold light installations you might know
              from other capitals — the city keeps it understated. What you get instead are the
              traditional decorations Copenhagen has done for decades, and honestly, they&apos;re all
              the more charming for it.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {DECOR.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.06} y={40}>
                <div
                  data-testid={`christmas-decor-${i}`}
                  className="bg-white/5 border border-white/15 p-5 flex items-start gap-4 h-full hover:bg-white/10 transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FDE047] text-black shrink-0">
                    <d.icon size={18} />
                  </div>
                  <div>
                    <h3 className="font-display font-900 tracking-tight text-xl leading-none">
                      {d.name}
                    </h3>
                    <span className="mt-1 inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-white/60">
                      <MapPin size={12} /> {d.where.toUpperCase()}
                    </span>
                    <p className="font-body text-sm text-white/85 mt-2 leading-relaxed">
                      {d.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* traditions & activities */}
      <section id="traditions" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                TRADITIONS &amp; EVENTS
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              The traditions worth<br />
              <span className="font-editorial italic font-600">planning around</span>
            </h2>
          </Reveal>

          <div className="mt-6 space-y-3">
            {ACTIVITIES.map((a, i) => (
              <Reveal key={a.name} delay={i * 0.05} y={30}>
                <div
                  data-testid={`christmas-activity-${i}`}
                  className="bg-[#F9F8F6] border border-zinc-200 shadow-brut p-4 md:p-5 flex flex-col md:flex-row md:items-start gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#DF3226] text-white shrink-0">
                    <a.icon size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                      <h3 className="font-display font-900 tracking-tight text-xl leading-none">
                        {a.name}
                      </h3>
                      <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226] shrink-0">
                        <CalendarDays size={13} /> {a.when.toUpperCase()}
                      </span>
                    </div>
                    <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed">
                      {a.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* gløgg — links out to the full guide */}
      <section id="glogg" className="bg-[#7A1420] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                THE GLØGG GUIDE
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Warm, spiced{" "}
              <span className="font-editorial italic font-600">&amp; everywhere</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/85 max-w-2xl mt-6 leading-relaxed">
              Gløgg is Denmark&apos;s spiced winter warmer — hot red wine steeped with cloves, cinnamon
              and orange peel, poured over raisins and almonds. A real gløgg is strong — nothing like
              the sweet &quot;mulled wine&quot; at the market stalls.
            </p>
            <Link
              to="/glogg"
              data-testid="christmas-glogg-guide-link"
              className="group mt-8 inline-flex items-center gap-2 font-display font-900 text-sm md:text-base bg-[#FDE047] text-black px-6 py-3.5 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
            >
              The gløgg guide
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* christmas food */}
      <section id="christmas-food" data-testid="christmas-food" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">CHRISTMAS FOOD</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              The December plate
            </h2>
          </Reveal>

          <Reveal y={20}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-8 md:mt-10">
              {[
                { img: "/images/food/flaeskesteg.webp", name: "Flæskesteg", alt: "Flæskesteg — Danish roast pork with crispy crackling" },
                { img: "/images/food/risengrod.webp", name: "Risengrød", alt: "Risengrød — hot rice porridge with butter, cinnamon and sugar" },
                { img: "/images/food/aebleskiver.webp", name: "Æbleskiver", alt: "Æbleskiver — round pancake puffs with powdered sugar and jam" },
                { img: "/images/food/risalamande.webp", name: "Risalamande", alt: "Risalamande — rice pudding with warm cherry sauce" },
              ].map((d) => (
                <figure key={d.name} data-testid={`christmas-plate-${d.name.toLowerCase()}`}>
                  <img
                    src={d.img}
                    alt={d.alt}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover rounded-xl border border-zinc-200 shadow-brut"
                  />
                  <figcaption className="mt-2 font-body font-700 text-xs md:text-sm text-black/60 text-center">
                    {d.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <Link
              to="/danish-food"
              data-testid="christmas-to-danish-food"
              className="group mt-6 inline-flex items-center gap-2 font-body font-800 text-sm md:text-base text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors"
            >
              Flæskesteg, stegt flæsk and the rest — the full Danish food guide
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <Link
              to="/christmas-food"
              data-testid="christmas-to-christmas-food"
              className="group mt-3 inline-flex items-center gap-2 font-body font-800 text-sm md:text-base text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors"
            >
              Hungry for the whole thing? 16 restaurants serve a traditional Danish Christmas
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* streets with lights */}
      <section className="bg-[#7A1420] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                STREETS LIT UP FOR CHRISTMAS
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl md:text-4xl max-w-3xl">
              Follow the lights{" "}
              <span className="font-editorial italic font-600">through the old town</span>
            </h2>
            <p className="font-body text-sm md:text-base text-white/80 mt-4 max-w-2xl leading-relaxed">
              Half the fun is simply wandering. These inner-city streets — most of them pedestrian —
              are strung with lights all season. Start on Strøget and let one lead you to the next.
            </p>
          </Reveal>
          <Reveal delay={0.1} y={30}>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="christmas-light-streets">
              {LIGHT_STREETS.map((s) => (
                <a
                  key={s}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s}, Copenhagen`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`christmas-street-${s}`}
                  className="inline-flex items-center gap-1.5 font-body font-800 text-xs bg-white/10 border border-white/20 px-3 py-1.5 hover:bg-[#FDE047] hover:text-black hover:border-black transition-all"
                >
                  <MapPin size={12} className="text-[#FDE047]" /> {s}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* the Christmas walk — one route through the markets */}
      <section id="christmas-walk" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <ChristmasWalkCard linkToGuide />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Coming for Christmas?<br />
            <span className="font-editorial italic font-600">Build your days around it</span>
          </h2>
          <Link
            to="/planner"
            data-testid="christmas-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      {/* Book a tour — Tivoli Gardens (magical at Christmas) */}
      <GygTourBand tourId="1427049" label="BOOK A TOUR" />

      <ExploreLinks
        testid="explore-christmas"
        links={[
          { label: "Where to eat it — 16 restaurants serving traditional Christmas food", to: "/christmas-food" },
          { label: "The Copenhagen gløgg guide — six glasses worth seeking out", to: "/glogg" },
          { label: "When to come — December in Copenhagen", to: "/copenhagen-in/december" },
        ]}
      />

      {/* Christmas food FAQ */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "What do Danes eat at Christmas?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Roast pork (flæskesteg) or roast duck (andesteg) with red cabbage and caramelised potatoes on the 24th — then risalamande, cold rice pudding with warm cherry sauce, one portion hiding an almond that wins a prize. All December long it's risengrød (hot rice porridge with butter, cinnamon and sugar) and æbleskiver pancake puffs at the markets.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Can visitors try Danish Christmas food?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes — many restaurants serve julefrokost (the long Danish Christmas lunch) through November and December. Ask for the 'juleplatte' and you get a bit of everything. Our Christmas dinner guide lists the restaurants serving traditional Christmas food this season.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "What are the best things to eat at the Copenhagen Christmas markets?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Real Danish æbleskiver — the bigger, properly round pancake puffs with jam and powdered sugar — and a cup of real Danish gløgg. And if you spot a flæskestegsandwich (roast pork with crackling in a bun), grab it: it's the market classic.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How cold is Copenhagen during the Christmas period?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Count on 1–5°C in December and 3–7°C in November, plus the harbour wind that makes it feel colder. Snow is possible but never guaranteed — the Christmas lights do the heavy lifting. Dress so you can linger: the markets are outdoors and the gløgg is hot.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Does it cost money to enter the Christmas markets in Copenhagen?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "No — all the city markets (Nyhavn, Kongens Nytorv, Højbro Plads, Gammel Torv and the rest) are free to wander. The one exception is Tivoli: the gardens charge their normal entrance fee, Christmas stalls included.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "When do Copenhagen's Christmas markets open in 2026?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "The season starts early: Nyhavn's stalls usually appear around 1 November, Gammel Torv opens officially on 6 November and the big City Hall Square market on Rådhuspladsen follows on 11 November. Tivoli's Christmas season runs 13 November to 3 January, Christiania's Grey Hall market is around 6–20 December, and Bakken in the deer forest does Christmas weekends from 21 November.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Which Copenhagen Christmas market is the best?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Tivoli is the showstopper — lights, stalls and rides. Nyhavn is the postcard one, stalls not that special but the scenery is. Gammel Torv and Rådhuspladsen the most central, Christiania's indoor Grey Hall market the cosy, handcrafted goods, alternative pick and Christmas at Bakken the out of town worthy trip for an alternative day.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How do Copenhagen's Christmas markets compare to other countries?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "It depends on what you're after, but Copenhagen at Christmas is down to earth — not overdone or flashy. Across the squares many stalls sell the same woollen hats, little figures and potatoes on a stick, with one exception: Christiania's Grey Hall market, which has more independent stalls and handmade items. You won't find the vast markets with hundreds of stalls like in Germany, or the giant million-light installations. Come instead for the cosiness — and the joy of real Danish gløgg and æbleskiver.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "When is J-Day in Copenhagen in 2026?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Friday 6 November at 20:59 sharp — the first Friday of November, when Tuborg releases the year's Julebryg Christmas beer. Bars across the city fill up and blue Santa hats appear everywhere. It's raucous, very Danish and the unofficial start of Christmas.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "How long do I need in Copenhagen for Christmas?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Two to three days is ideal. Two days covers the Christmas walk — Nyhavn, the square markets and Tivoli at night — plus a julefrokost lunch, the Christiania market or a Bakken Christmas day trip.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "When is the best time to visit Tivoli Gardens during Christmas?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Weekday afternoons — noticeably fewer people, and you still get the lights once darkness falls around four. Weekends are usually busy, especially the Fridays and Saturdays in December. Either way, arrive before dark so you see the gardens in daylight and lit up.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Is there an ice-skating rink in Copenhagen during Christmas?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes — you can skate at Broens Skøjtebane near Nyhavn, on Kongens Nytorv, and at the rink at Frederiksberg Runddel from late November.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Is Copenhagen a good city for Christmas shopping?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Very — Strøget and its side streets cover the big brands, Magasin du Nord and Illum do proper department-store Christmas, and the Latin Quarter and Jægersborggade have the independents for one-off gifts. The market stalls handle the handmade bits, and the Danish classics — Lego, Royal Copenhagen, salty liquorice — are easy wins to bring home.",
                    },
                  },
                ],
              })}
            </script>
          </Helmet>
          <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
            Christmas, <span className="font-editorial italic font-600">answered</span>
          </h2>
          <div className="max-w-3xl divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            <details data-testid="christmas-food-faq-0" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                What do Danes eat at Christmas?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Roast pork (flæskesteg) or roast duck (andesteg) with red cabbage and caramelised
                potatoes on the 24th — then risalamande, cold rice pudding with warm cherry sauce,
                one portion hiding an almond that wins a prize. All December long it&apos;s risengrød
                (hot rice porridge with butter, cinnamon and sugar) and æbleskiver at the markets.{" "}
                <Link to="/danish-food" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  The Danish food classics guide
                </Link>{" "}
                has the lot.
              </p>
            </details>
            <details data-testid="christmas-food-faq-1" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                Can visitors try Danish Christmas food?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Yes — many restaurants serve <strong>julefrokost</strong> (the long Danish Christmas
                lunch) through November and December. Ask for the <em>juleplatte</em> and you get a
                bit of everything.{" "}
                <Link to="/christmas-food" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  The Christmas dinner guide
                </Link>{" "}
                has the restaurants serving it this season.
              </p>
            </details>
            <details data-testid="christmas-food-faq-2" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                What are the best things to eat at the Copenhagen Christmas markets?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Real Danish <strong>æbleskiver</strong> — the bigger, properly round pancake puffs with
                jam and powdered sugar — and a cup of{" "}
                <Link to="/glogg" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  real Danish gløgg
                </Link>
                . And if you spot a <strong>flæskestegsandwich</strong> (roast pork with crackling in a
                bun), grab it: it&apos;s the market classic.
              </p>
            </details>
            <details data-testid="christmas-food-faq-3" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                How cold is Copenhagen during the Christmas period?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Count on 1–5°C in December and 3–7°C in November, plus the harbour wind that makes it
                feel colder. Snow is possible but never guaranteed — the Christmas lights do the heavy
                lifting. Dress so you can linger: the markets are outdoors and the gløgg is hot.
              </p>
            </details>
            <details data-testid="christmas-food-faq-4" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                Does it cost money to enter the Christmas markets in Copenhagen?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                No — all the city markets (Nyhavn, Kongens Nytorv, Højbro Plads, Gammel Torv and the
                rest) are free to wander. The one exception is <strong>Tivoli</strong>: the gardens
                charge their normal entrance fee, Christmas stalls included.
              </p>
            </details>
            <details data-testid="christmas-food-faq-5" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                When do Copenhagen&apos;s Christmas markets open in 2026?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                The season starts early: <strong>Nyhavn&apos;s</strong> stalls usually appear around 1
                November, <strong>Gammel Torv</strong> opens officially on 6 November and the big City
                Hall Square market on <strong>Rådhuspladsen</strong> follows on 11 November.{" "}
                <strong>Tivoli&apos;s</strong> Christmas season runs 13 November to 3 January,{" "}
                <strong>Christiania&apos;s</strong> Grey Hall market is around 6–20 December, and{" "}
                <strong>Bakken</strong> in the deer forest does Christmas weekends from 21 November.
              </p>
            </details>
            <details data-testid="christmas-food-faq-6" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                Which Copenhagen Christmas market is the best?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                <strong>Tivoli</strong> is the showstopper — lights, stalls and rides.{" "}
                <strong>Nyhavn</strong> is the postcard one, stalls not that special but the scenery
                is. <strong>Gammel Torv</strong> and <strong>Rådhuspladsen</strong> the most central,{" "}
                <strong>Christiania&apos;s</strong> indoor Grey Hall market the cosy, handcrafted
                goods, alternative pick and{" "}
                <Link to="/trips/bakken" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  Christmas at Bakken
                </Link>{" "}
                the out of town worthy trip for an alternative day.
              </p>
            </details>
            <details data-testid="christmas-food-faq-compare" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                How do Copenhagen&apos;s Christmas markets compare to other countries?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                It depends on what you&apos;re after, but Copenhagen at Christmas is down to earth —
                not overdone or flashy. Across the squares many stalls sell the same woollen hats,
                little figures and potatoes on a stick, with one exception:{" "}
                <strong>Christiania&apos;s</strong> Grey Hall market, which has more independent
                stalls and handmade items. You won&apos;t find the vast markets with hundreds of
                stalls like in Germany, or the giant million-light installations. Come instead for
                the cosiness — and the joy of real Danish{" "}
                <Link to="/glogg" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  gløgg
                </Link>{" "}
                and{" "}
                <Link to="/danish-food" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  æbleskiver
                </Link>
                .
              </p>
            </details>
            <details data-testid="christmas-food-faq-7" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                When is J-Day in Copenhagen in 2026?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Friday 6 November at 20:59 sharp — the first Friday of November, when Tuborg releases
                the year&apos;s Julebryg Christmas beer. Bars across the city fill up and blue Santa
                hats appear everywhere. It&apos;s raucous, very Danish and the unofficial start of
                Christmas.
              </p>
            </details>
            <details data-testid="christmas-food-faq-8" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                How long do I need in Copenhagen for Christmas?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Two to three days is ideal. Two days covers{" "}
                <Link to="/christmas-walk" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  the Christmas walk
                </Link>{" "}
                — Nyhavn, the square markets and Tivoli at night — plus a julefrokost lunch, the
                Christiania market or a{" "}
                <Link to="/trips/bakken" className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80">
                  Bakken Christmas day trip
                </Link>
                .
              </p>
            </details>
            <details data-testid="christmas-food-faq-9" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                When is the best time to visit Tivoli Gardens during Christmas?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                <strong>Weekday afternoons</strong> — noticeably fewer people, and you still get the
                lights once darkness falls around four. Weekends are usually busy, especially the
                Fridays and Saturdays in December. Either way, arrive before dark so you see the
                gardens in daylight and lit up.
              </p>
            </details>
            <details data-testid="christmas-food-faq-10" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                Is there an ice-skating rink in Copenhagen during Christmas?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Yes — you can skate at <strong>Broens Skøjtebane</strong> near Nyhavn, on{" "}
                <strong>Kongens Nytorv</strong>, and at the rink at{" "}
                <strong>Frederiksberg Runddel</strong> from late November.
              </p>
            </details>
            <details data-testid="christmas-food-faq-11" className="group py-5">
              <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                Is Copenhagen a good city for Christmas shopping?
                <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                Very — <strong>Strøget</strong> and its side streets cover the big brands,{" "}
                <strong>Magasin du Nord</strong> and <strong>Illum</strong> do proper department-store
                Christmas, and the <strong>Latin Quarter</strong> and <strong>Jægersborggade</strong>{" "}
                have the independents for one-off gifts. The market stalls handle the handmade bits,
                and the Danish classics — Lego, Royal Copenhagen, salty liquorice — are easy wins to
                bring home.
              </p>
            </details>
          </div>
        </div>
      </section>

      <ShareBar />
    </main>
  );
}
